import React, { useState } from 'react'
import { useTranslation } from 'language/translator'

import { KpiOptionObject } from 'types/GlobalKpiOption'
import { TWidgetSettings } from 'types/GlobalWidget'

import SettingsGroup from 'components_new/molecules/SettingsGroup'
import SettingsEditDialog from 'components_new/molecules/SettingsEditDialog'
import { TInputType } from 'components_new/molecules/SettingsEditDialog/utils'
import SettingsItem from 'components_new/molecules/SettingsItem'
import TrendTag from 'components_new/molecules/TrendTag'

import ThresholdSection from './ThresholdSection'
import { Option } from 'components_new/molecules/SelectSearch'
import { TPatchKpiOptionBody } from 'redux/api/KpiOptions'

interface IndicationSectionProps {
  kpi: KpiOptionObject
  onClose: () => void
  settings: TWidgetSettings
  updateKpiOption: (body: TPatchKpiOptionBody) => void
}

const IndicationSection = (props: IndicationSectionProps) => {
  const { kpi, onClose, settings, updateKpiOption } = props
  const { t } = useTranslation()

  /*-- dialogs --*/
  const [dialog, setDialog] = useState<{
    open: boolean
    property: TProperty
    title: string
    data: {
      options: Option[]
      type: TInputType
      value: string
    }
  }>({
    open: false,
    property: 'increase_is_positive',
    title: '',
    data: {
      options: [],
      type: 'text',
      value: ''
    }
  })

  // Properties edited in this component.
  type TProperty = 'increase_is_positive'

  const getPropertyTitle = (property: TProperty) => {
    switch (property) {
    case 'increase_is_positive':
      return t('widgets', 'increase_is')
    }
  }

  const handleCloseDialog = () => {
    setDialog((prevState) => ({ ...prevState, open: false }))
  }

  const handleOpenDialog = (property: TProperty) => {
    let inputValue = ''
    let inputType: TInputType = 'text'
    let inputOptions: Option[] = []

    switch (property) {
    case 'increase_is_positive':
      inputValue = kpi.increase_is_positive.toString()
      inputType = 'select'
      inputOptions = [
        { title: t('widgets', 'positive'), id: 'true' },
        { title: t('widgets', 'negative'), id: 'false' }
      ]
      break
    }

    setDialog({
      open: true,
      property: property,
      title: getPropertyTitle(property),
      data: {
        options: inputOptions,
        type: inputType,
        value: inputValue
      }
    })
  }

  const handleEditDialogValue = (value: string) => {
    setDialog((prevState) => ({
      ...prevState,
      data: { ...prevState.data, value }
    }))
  }

  const handleSubmitDialog = () => {
    updateKpiOption({
      increase_is_positive: dialog.data.value === 'true'
    })
  }

  return (
    <>
      <SettingsGroup title={t('widgets', 'indication_title')}>
        <ThresholdSection
          kpi={kpi}
          onClose={onClose}
          settings={settings}
          updateKpiOption={updateKpiOption}
        />
        <SettingsItem
          actions={
            <>
              <TrendTag
                increaseIsPositive={kpi.increase_is_positive}
                value={10}
              />
              <TrendTag
                increaseIsPositive={kpi.increase_is_positive}
                value={-10}
              />
            </>
          }
          onClick={() => handleOpenDialog('increase_is_positive')}
          title={getPropertyTitle('increase_is_positive')}
          value={
            kpi.increase_is_positive
              ? t('widgets', 'positive')
              : t('widgets', 'negative')
          }
          variant="edit"
        />
      </SettingsGroup>

      {/*-- dialogs --*/}
      <SettingsEditDialog
        onChange={handleEditDialogValue}
        onClose={handleCloseDialog}
        onSubmit={handleSubmitDialog}
        open={dialog.open}
        options={dialog.data.options}
        title={dialog.title}
        type={dialog.data.type}
        value={dialog.data.value}
      />
    </>
  )
}

export default IndicationSection
